/**
 * AutoTarget — seleção automática de alvo (mobile: o jogador não clica no inimigo).
 * Prioriza: (1) alvo atual se ainda válido, (2) inimigo vivo mais próximo no alcance.
 * Usa histerese (dropHysteresis) e um refresh interval para evitar troca frenética de alvo.
 */
import { GameConfig } from "../config/GameConfig";
import type { Damageable } from "./Damageable";

export class AutoTarget {
  current: Damageable | null = null;
  private timer = 0;
  private readonly cfg = GameConfig.autoTarget;

  /**
   * Reavalia o alvo (barato) e retorna o alvo atual. `isValid` (opcional) é o filtro
   * central de alvo válido (ex.: descartar alvos dentro da Sanctuary). Se um alvo deixa
   * de ser válido, é largado imediatamente.
   */
  update(
    dt: number,
    playerPos: { x: number; z: number },
    candidates: readonly Damageable[],
    isValid?: (t: Damageable) => boolean,
    inReach?: (t: Damageable) => boolean,
  ): Damageable | null {
    this.timer -= dt;

    // 1) mantém o alvo atual enquanto for válido (vivo, presente e dentro do alcance + histerese)
    if (this.current) {
      const stillThere = candidates.indexOf(this.current) !== -1;
      if (
        !stillThere ||
        this.current.isDead ||
        (isValid && !isValid(this.current)) ||
        !this.within(this.current, playerPos, this.cfg.radius + this.cfg.dropHysteresis)
      ) {
        this.current = null;
      }
    }

    // 1b) 10.9HQ — Thiago: "andando, para de bater". A histerese segurava o alvo que FICOU PRA TRÁS
    // (até 11,5 m) enquanto outro bicho passava colado no Caçador: o auto-ataque só olha o alvo atual,
    // então não batia em ninguém até parar e o alvo velho alcançar. Agora, se o alvo atual está FORA
    // do alcance da arma e existe outro válido DENTRO, troca na hora (o mais perto).
    if (this.current && inReach && !inReach(this.current)) {
      const near = this.nearest(playerPos, candidates, (t) => (!isValid || isValid(t)) && inReach(t));
      if (near) this.current = near;
    }
    // 2) sem alvo: adquire o mais próximo dentro do alcance (respeitando o refresh)
    if (!this.current && this.timer <= 0) {
      this.timer = this.cfg.refreshInterval;
      this.current = this.nearest(playerPos, candidates, isValid);
    }
    return this.current;
  }

  private nearest(
    playerPos: { x: number; z: number },
    candidates: readonly Damageable[],
    isValid?: (t: Damageable) => boolean,
  ): Damageable | null {
    let best: Damageable | null = null;
    let bestD = this.cfg.radius * this.cfg.radius;
    for (const c of candidates) {
      if (c.isDead) continue;
      if (isValid && !isValid(c)) continue;
      const dx = c.position.x - playerPos.x;
      const dz = c.position.z - playerPos.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD) {
        bestD = d2;
        best = c;
      }
    }
    return best;
  }

  private within(t: Damageable, playerPos: { x: number; z: number }, r: number): boolean {
    const dx = t.position.x - playerPos.x;
    const dz = t.position.z - playerPos.z;
    return dx * dx + dz * dz <= r * r;
  }

  clear(): void {
    this.current = null;
    this.timer = 0;
  }
}
